import type Mithril from 'mithril';
import UserPage, { IUserPageAttrs } from 'flarum/forum/components/UserPage';
import CommentPost from 'flarum/forum/components/CommentPost';
import type Post from 'flarum/common/models/Post';
import type User from 'flarum//common/models/User';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import Placeholder from 'flarum/common/components/Placeholder';
import dayjs from 'dayjs';

// 类别类型: 1->有效期 2->到期生效 3->永久
enum PointCategoryTypeEnum {
  BEFORE_EXPIRY_DATE = 1,
  AFTER_EXPIRY_DATE = 2,
  FOREVER = 3,
}

// 动作类型: 1->收入 2->支出
enum PointActionTypeEnum {
  INCOME = 1,
  EXPENSES = 2,
}

// 积分详情
interface IPointDetails {
  id: number;
  userId: number;
  username: string;
  nickname: string;
  categoryId: number;
  categoryName: string;
  points: number;
  pointType: PointCategoryTypeEnum;
  remainPoints?: number; // 剩余积分
  action: PointActionTypeEnum; // 动作类型
  businessId?: number; // 业务ID 目前应该是指消耗的订单ID
  description?: string; // 交易描述
  expireTime: string; // 过期时间
  createTime: string;
  updateTime: string;
}

const POINT_CATEGORY_TYPE_TITLES = {
  [PointCategoryTypeEnum.BEFORE_EXPIRY_DATE]: '有效期',
  [PointCategoryTypeEnum.AFTER_EXPIRY_DATE]: '到期生效',
  [PointCategoryTypeEnum.FOREVER]: '立即生效',
};

const POINT_ACTION_TYPE_TITLES = {
  [PointActionTypeEnum.INCOME]: '收入',
  [PointActionTypeEnum.EXPENSES]: '支出',
};

export default class PointDetailsPage extends UserPage {
  loading: boolean = true;

  moreResults: boolean = false;

  loadLimit: number = 20;

  data: IPointDetails[] = [];

  oninit(vnode: Mithril.Vnode<IUserPageAttrs, this>) {
    super.oninit(vnode);

    this.loadUser(m.route.param('username'));
  }

  content() {
    if (this.data.length === 0 && !this.loading) {
      return (
        <div className="PostsUserPage">
          <Placeholder text={app.translator.trans('core.forum.user.posts_empty_text')} />
        </div>
      );
    }

    let footer;

    if (this.loading) {
      footer = <LoadingIndicator />;
    } else if (this.moreResults) {
      footer = (
        <div className="PostsUserPage-loadMore">
          <Button className="Button" onclick={this.loadMore.bind(this)}>
            {app.translator.trans('core.forum.user.posts_load_more_button')}
          </Button>
        </div>
      );
    }

    return (
      <div className="PostsUserPage">
        <ul className="PostsUserPage-list">
          {this.data.map((item) => (
            <li className="PointDetailsPage-list-item">
              <div>
                <div>时间: {item.createTime ? dayjs(item.createTime).format('YYYY-MM-DD HH:mm:ss') : '-'}</div>
                <div>名称: {item.categoryName}</div>
                {/*<div>类别: {POINT_CATEGORY_TYPE_TITLES[item.pointType]}</div>*/}
              </div>
              <div>
                <div>
                  数量: {item.action === PointActionTypeEnum.INCOME ? '+' : '-'}
                  {item.points} {POINT_ACTION_TYPE_TITLES[item.action]}
                </div>
                <div>描述: {item.description}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="PostsUserPage-loadMore">{footer}</div>
      </div>
    );
  }

  /**
   * Initialize the component with a user, and trigger the loading of their
   * activity feed.
   */
  show(user: User): void {
    super.show(user);

    this.refresh();
  }

  /**
   * Clear and reload the user's activity feed.
   */
  refresh() {
    this.loading = true;
    this.data = [];

    m.redraw();

    this.loadResults().then(this.parseResults.bind(this));
  }

  /**
   * Load a new page of the user's activity feed.
   *
   * @protected
   */
  async loadResults(offset = 0) {
    if (!this.user) {
      return [];
    }
    const pageNum = Math.max(Math.ceil(offset / this.loadLimit), 1);
    const pageSize = this.loadLimit;
    const username = this.user.slug();
    return fetch(`/mall-api/pointDetails?pageNum=${pageNum}&pageSize=${pageSize}&username=${username}`)
      .then((response) => response.json())
      .then((result) => {
        return result?.data?.list || [];
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
    // return app
    //   .request({
    //     method: 'GET',
    //     url: 'http://localhost:8002/mall-api/pointDetails',
    //     // url: '/mall-api/pointDetails',
    //     params: {
    //       pageNum,
    //       pageSize: this.loadLimit,
    //       username: this.user.slug(),
    //     },
    //   })
  }

  /**
   * Load the next page of results.
   */
  loadMore() {
    this.loading = true;
    this.loadResults(this.data.length).then(this.parseResults.bind(this));
  }

  /**
   * Parse results and append them to the activity feed.
   */
  parseResults(results: IPointDetails[]): IPointDetails[] {
    this.loading = false;

    this.data.push(...results);

    this.moreResults = results.length >= this.loadLimit;
    m.redraw();

    return results;
  }
}
